import React, { useCallback, useId, useMemo } from 'react';

import styles from './Pagination.module.scss';

/**
 * ページネーションの表示番号リストを生成する。
 * 現在ページの前後1ページを表示し、離れたページは `null`（省略記号）で埋める。
 * @param current - 現在のページ番号（1始まり）
 * @param total   - 総ページ数
 * @returns ページ番号と省略位置（null）の配列
 */
export const buildPageNumbers = (
  current: number,
  total: number,
): (number | null)[] => {
  const pages: (number | null)[] = [];
  const delta = 1;
  const left = current - delta;
  const right = current + delta;

  let last = 0;

  Array.from({ length: total }, (_, i) => i + 1).forEach((p) => {
    if (p === 1 || p === total || (p >= left && p <= right)) {
      if (last > 0 && p - last > 1) pages.push(null);
      pages.push(p);
      last = p;
    }
  });

  return pages;
};

/**
 * プロパティ
 */
export type PaginationProps = {
  /** 現在のページ番号（1始まり） */
  page: number;
  /** 総ページ数 */
  totalPages: number;
  /** ページあたりの表示件数 */
  pageSize: number;
  /** ページサイズの選択肢 */
  pageSizeOptions: readonly number[];
  /** フィルタ後の総件数 */
  totalCount: number;
  /** ページサイズセレクタのラベル文字列 */
  pageSizeLabel: string;
  /** 件数表示のサフィックス（例: "件"） */
  totalCountUnit: string;
  /** ページ移動ハンドラー */
  onPageChange: (page: number) => void;
  /** ページサイズ変更ハンドラー */
  onPageSizeChange: (size: number) => void;
};

/**
 * ページネーションコンポーネント。
 * ページサイズ切替・件数表示・ページ移動を提供する。 */
export const Pagination = React.memo(
  ({
    page,
    totalPages,
    pageSize,
    pageSizeOptions,
    totalCount,
    pageSizeLabel,
    totalCountUnit,
    onPageChange,
    onPageSizeChange,
  }: PaginationProps) => {
    const pageSizeId = useId();

    /**
     * ページサイズセレクタ変更
     */
    const handlePageSizeChange = useCallback(
      ({ target }: React.ChangeEvent<HTMLSelectElement>) => {
        onPageSizeChange(Number(target.value));
      },
      [onPageSizeChange],
    );

    /**
     * 先頭ページへ移動
     */
    const handleFirstPage = useCallback(() => onPageChange(1), [onPageChange]);

    /**
     * 前ページへ移動
     */
    const handlePrevPage = useCallback(
      () => onPageChange(Math.max(1, page - 1)),
      [onPageChange, page],
    );

    /**
     * ページ番号ボタンクリック。
     * `data-page` 属性から移動先を取得する。
     */
    const handlePageClick = useCallback(
      ({ currentTarget }: React.MouseEvent<HTMLButtonElement>) => {
        const p = Number(currentTarget.dataset.page);

        if (p) onPageChange(p);
      },
      [onPageChange],
    );

    /**
     * 次ページへ移動
     */
    const handleNextPage = useCallback(
      () => onPageChange(Math.min(totalPages, page + 1)),
      [onPageChange, totalPages, page],
    );

    /**
     * 末尾ページへ移動
     */
    const handleLastPage = useCallback(
      () => onPageChange(totalPages),
      [onPageChange, totalPages],
    );

    /**
     * 表示するページ番号リスト。
     * page・totalPages が変わるまで再計算しない。
     */
    const pageNumbers = useMemo(
      () => buildPageNumbers(page, totalPages),
      [page, totalPages],
    );

    return (
      <div className={styles.pagination}>
        <div className={styles['pagination-left']}>
          <label className={styles['page-size-label']} htmlFor={pageSizeId}>
            {pageSizeLabel}
          </label>
          <select
            id={pageSizeId}
            className={styles['page-size-select']}
            value={pageSize}
            onChange={handlePageSizeChange}
          >
            {pageSizeOptions.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <span className={styles['page-range']}>
            {totalCount === 0
              ? `0 / 0${totalCountUnit}`
              : `${(page - 1) * pageSize + 1}-${Math.min(
                  page * pageSize,
                  totalCount,
                )} / ${totalCount}${totalCountUnit}`}
          </span>
        </div>

        {totalPages > 1 && (
          <div className={styles['pagination-nav']}>
            <button
              type="button"
              className={styles['page-btn']}
              onClick={handleFirstPage}
              disabled={page === 1}
            >
              {'|<'}
            </button>
            <button
              type="button"
              className={styles['page-btn']}
              onClick={handlePrevPage}
              disabled={page === 1}
            >
              {'<'}
            </button>
            {pageNumbers.map((p, i, nums) =>
              p === null ? (
                <span
                  key={`ellipsis-before-${nums[i + 1]}`}
                  className={styles['page-ellipsis']}
                >
                  …
                </span>
              ) : (
                <button
                  key={p}
                  type="button"
                  className={`${styles['page-num']} ${
                    p === page ? styles['page-num--active'] : ''
                  }`}
                  data-page={p}
                  onClick={handlePageClick}
                >
                  {p}
                </button>
              ),
            )}
            <button
              type="button"
              className={styles['page-btn']}
              onClick={handleNextPage}
              disabled={page === totalPages}
            >
              {'>'}
            </button>
            <button
              type="button"
              className={styles['page-btn']}
              onClick={handleLastPage}
              disabled={page === totalPages}
            >
              {'>|'}
            </button>
          </div>
        )}
      </div>
    );
  },
);
